import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  OrganizationComplianceProfile,
  UpdateComplianceProfilePayload,
} from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

export function useComplianceProfile() {
  return useQuery({
    queryKey: queryKeys.complianceProfile.data(),
    queryFn: async (): Promise<OrganizationComplianceProfile> => {
      const response = await apiClient.get<OrganizationComplianceProfile>(
        '/api/v1/compliance/profile/'
      );
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useUpdateComplianceProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: UpdateComplianceProfilePayload
    ): Promise<OrganizationComplianceProfile> => {
      const response = await apiClient.patch<OrganizationComplianceProfile>(
        '/api/v1/compliance/profile/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceProfile.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
    },
  });
}

export interface ResetComplianceDefaultsResponse {
  reset: boolean;
  industry: string;
  profile: OrganizationComplianceProfile;
}

export function useResetComplianceDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ResetComplianceDefaultsResponse> => {
      const response = await apiClient.post<ResetComplianceDefaultsResponse>(
        '/api/v1/compliance/profile/reset-defaults/'
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceProfile.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
    },
  });
}
